import React, { useState } from 'react'
import Figure from './Figure'
import Highlight, { defaultProps } from 'prism-react-renderer'
import { format, formatDistance, differenceInDays } from 'date-fns'
import tw, { css, theme } from 'twin.macro'
import SanityImage from 'gatsby-plugin-sanity-image'
import Cloud from './Cloud'
import Rating from './rating'
import RatingHollow from './rating-hollow'
import Tilt from 'react-parallax-tilt'
import { ExternalLinkIcon } from '@heroicons/react/solid'

const serializers = {
  types: {
    authorReference: ({ node }) => <span>{node.author.name}</span>,
    // reviewReference output
    reviewReference: ({ node }) => {
      return (
        <div tw="flex flex-row items-start">
          <div tw="pr-4 lg:p-4 flex-grow w-full">
            <div
              tw="text-xl lg:text-2xl xl:text-3xl m-0 underline"
              css={{
                '&:hover': {
                  color: 'var(--color-primary)',
                },
              }}
            >
              <a href={'/reviews/' + node.review.slug.current} target="_blank">
                {node.review.title}
              </a>
            </div>
            {node.review.rating && (
              <figure
                tw="leading-none py-2"
                css={{
                  margin: '0 !important',
                }}
              >
                <div tw="inline-flex space-x-1">
                  <Rating rating={node.review.rating} />
                  <RatingHollow rating={node.review.rating} />
                </div>
                <figcaption tw="font-sans sr-only">
                  Rating: {node.review.rating} out of 5
                </figcaption>
              </figure>
            )}
            <p
              tw="leading-normal"
              css={{
                margin: '0 !important',
              }}
            >
              {node.review.excerpt[0].children[0].text}
            </p>
            <small tw="font-sans text-xs text-softer opacity-80 leading-normal">
              Last updated on
              {differenceInDays(new Date(node.review.publishedAt), new Date()) > 3
                ? formatDistance(new Date(node.review.publishedAt), new Date())
                : format(new Date(node.review.publishedAt), ' do MMM yyyy')}
            </small>
          </div>
          <div className="group">
            <a tw="block" href={'/reviews/' + node.review.slug.current} target="_blank">
              {node.review.mainImage && node.review.mainImage.asset._ref.includes('png') && (
                <SanityImage
                  {...node.review.mainImage}
                  tw="transform transition duration-150 ease-in-out scale-75 md:scale-90 motion-safe:group-hover:scale-90 md:motion-safe:group-hover:scale-100 -rotate-6 group-hover:rotate-0 max-h-full object-cover w-48"
                  css={{
                    margin: '0 auto !important',
                    '&[data-lqip]': {
                      objectFit: 'cover',
                    },
                  }}
                />
              )}
              {node.review.mainImage && !node.review.mainImage.asset._ref.includes('png') && (
                <Tilt
                  tiltReverse={true}
                  tiltMaxAngleX="10.0"
                  tiltMaxAngleY="10.0"
                  gyroscope={true}
                  css={{ height: 'inherit' }}
                >
                  <SanityImage
                    {...node.review.mainImage}
                    tw="m-auto w-48 scale-90 max-h-full object-contain shadow-sm border rounded bg-white"
                    css={{
                      '&[data-lqip]': {
                        width: '100%',
                      },
                    }}
                  />
                </Tilt>
              )}
            </a>
          </div>
        </div>
      )
    },
    linkBlock: ({ node }) => {
      return (
        <div className="link-block" tw="flex flex-col-reverse md:pt-16" id={node.slug.current}>
          <div
            className="caption"
            tw="py-4 flex flex-col-reverse md:flex-row justify-between items-baseline md:space-x-4"
          >
            {node.linkDescription && (
              <div
                tw="border-t md:border-0 w-full md:w-auto md:max-w-md font-sans text-sm opacity-80 text-softer pt-2 md:py-1"
                css={{
                  borderColor: 'var(--color-border)',
                }}
              >
                <small tw="font-sans text-sm opacity-80 md:flex-auto py-4 md:py-0 text-softer">
                  {node.linkDescription}
                </small>
              </div>
            )}
            <div tw="flex-grow md:flex-grow-0 w-full md:w-auto">
              <a
                href={node.url}
                tw="rounded-full py-2 px-4 text-center font-medium font-sans block md:inline-block mb-4 sm:mb-0 cursor-pointer text-softer leading-normal text-base no-underline"
                target="_blank"
                css={{
                  backgroundColor: 'transparent',
                  color: `var(--text-stronger) !important`,
                  boxShadow: '0 0 0 1px var(--color-border), 0 1px 1px 1px var(--color-border)',
                  '&:hover, &:focus': {
                    backgroundColor: 'var(--bg-secondary)',
                  },
                  textDecoration: 'none !important',
                  '&:focus': {
                    backgroundColor: 'var(--bg-secondary)',
                  },
                }}
              >
                {node.linkTitle}
                <ExternalLinkIcon
                  tw="inline-block ml-1.5 -mt-0.5"
                  css={{
                    width: 16,
                    height: 16,
                  }}
                />
              </a>
            </div>
          </div>
          {node.linkImage && node.linkImage.asset && (
            <SanityImage
              {...node.linkImage}
              tw="m-auto md:shadow-sm border-t border-b md:border md:rounded w-full"
              css={{
                '&[data-lqip]': {
                  width: '100%',
                },
              }}
              style={{
                margin: '0 auto',
                objectFit: 'cover',
                borderColor: 'var(--color-border)',
              }}
            />
          )}
          <div
            tw="py-4 px-6 sm:px-0"
            css={{
              maxWidth: 'min(650px, calc(100%-48px)) !important',
              width: '100%',
              margin: '0 auto',
            }}
          >
            <h2
              tw="md:pb-0"
              css={{
                margin: '0 !important',
              }}
            >
              {node.linkTitle}
            </h2>
          </div>
        </div>
      )
    },
    mainImage: Figure,
    cloudinary: Cloud,
    code: ({ node = {} }) => {
      const { code, language } = node
      const className = node.className || 'language-text'

      const CopyButton = props => {
        const [text, setText] = useState('Copy')

        return (
          <button
            tw="self-center"
            className="code-copy-button"
            onClick={() => {
              navigator.clipboard.writeText(props.content)
              setText('Copied!')
              setTimeout(() => {
                setText('Copy')
              }, 3000)
            }}
          >
            {text}
          </button>
        )
      }

      if (!node) {
        return null
      }
      return (
        <div
          className="shadow-sm gatsby-highlight"
          css={{
            borderRadius: '4px 4px 0 0',
            boxShadow: 'inset 0 0 0 1px var(--bg-secondary)',
            gridColumn: '1/-1 !important',
            backgroundColor: `${theme`backgroundColor.secondary`}`,
            maxWidth: 'min(1024px, calc(100% - 48px))',
            margin: '0 auto',
            width: '100%',
            marginTop: '1rem',
          }}
        >
          <div
            className="code-header"
            tw="flex flex-row items-stretch"
            css={{
              content: '"⌨️"',
              color: 'var(--textPrimary)',
              padding: '1rem',
              fontSize: '1rem',
              fontFamily: 'monospace',
              textAlign: 'center',
              width: '100%',
              height: 40,
              backgroundColor: 'var(--bg-lighter)',
              boxShadow: 'inset 0 0 0 1px var(--bg-secondary)',
              backgroundImage: 'linear-gradient(45deg, var(--bg-secondary), var(--bg-lighter))',
              borderRadius: '4px 4px 0 0',
            }}
          >
            {/* <span tw="self-center" className="language-name">{language}</span> */}
            <CopyButton content={code} />
          </div>
          <Highlight {...defaultProps} language={language ? language : ''} code={code} theme="">
            {({ className, tokens, getLineProps, getTokenProps }) => (
              <pre className={className}>
                <code className={className}>
                  {tokens.map((line, i) => (
                    <div className="code-block" key={i} {...getLineProps({ line, key: i })}>
                      <span className="line-number">{i + 1}</span>
                      {line.map((token, key) => (
                        <span key={key} {...getTokenProps({ token, key })} />
                      ))}
                    </div>
                  ))}
                </code>
              </pre>
            )}
          </Highlight>
        </div>
      )
    },
    block(props) {
      switch (props.node.style) {
        case 'h1':
          return <h1>{props.children}</h1>
        case 'h2':
          return <h2>{props.children}</h2>
        case 'h3':
          return <h3>{props.children}</h3>
        case 'h4':
          return <h4>{props.children}</h4>
        case 'h5':
          return <h5>{props.children}</h5>
        case 'h6':
          return <h6>{props.children}</h6>
        case 'blockquote':
          return <blockquote>{props.children}</blockquote>
        case 'aside':
          return (
            <aside>
              <p>{props.children}</p>
            </aside>
          )
        default:
          return <p>{props.children}</p>
      }
    },
  },
  marks: {
    link: ({ children, mark }) => <a href={mark.href}>{children}</a>,
  },
}

export default serializers
